import { useEffect, useState } from 'react';
import { pullDailyCard, type PullResponse } from './api';
import { CardView } from './components/CardView';
import { UpsellPanel } from './components/UpsellPanel';
import { Landing } from './components/Landing';

function hasTelegramInitData(): boolean {
  // @ts-expect-error — Telegram injects WebApp global
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
}

const FALLBACK_COPY: Record<string, {
  title: string;
  loading: string;
  error: string;
  already: string;
  browserTitle: string;
  browserBody: string;
  openInTelegram: string;
}> = {
  pl: {
    title: 'Karta dnia',
    loading: 'Tasuję talię…',
    error: 'Nie udało się wylosować karty.',
    already: 'Twoja dzisiejsza karta',
    browserTitle: 'Otwórz w Telegramie',
    browserBody: 'Tarot działa wyłącznie wewnątrz aplikacji Telegram — tam rozpoznajemy Twoje konto, zapisujemy historię i wysyłamy codzienną kartę o 9:00.',
    openInTelegram: 'Otwórz w Telegramie',
  },
  en: {
    title: 'Card of the day',
    loading: 'Shuffling…',
    error: 'Could not draw card.',
    already: 'Your card for today',
    browserTitle: 'Open in Telegram',
    browserBody: 'Tarot runs only inside Telegram — that\'s where we recognize your account, save history, and send your daily card at 9:00.',
    openInTelegram: 'Open in Telegram',
  },
  uk: {
    title: 'Карта дня', loading: 'Тасую колоду…', error: 'Не вдалося витягнути карту.', already: 'Ваша карта на сьогодні',
    browserTitle: 'Відкрийте в Telegram',
    browserBody: 'Таро працює лише в додатку Telegram — там ми впізнаємо ваш акаунт, зберігаємо історію та надсилаємо карту дня о 9:00.',
    openInTelegram: 'Відкрити в Telegram',
  },
  ru: {
    title: 'Карта дня', loading: 'Тасую колоду…', error: 'Не удалось вытянуть карту.', already: 'Ваша карта на сегодня',
    browserTitle: 'Откройте в Telegram',
    browserBody: 'Таро работает только в приложении Telegram — там мы узнаём ваш аккаунт, сохраняем историю и присылаем карту дня в 9:00.',
    openInTelegram: 'Открыть в Telegram',
  },
  hi: {
    title: 'आज का कार्ड', loading: 'कार्ड फेंट रहे हैं…', error: 'कार्ड नहीं खींच सके।', already: 'आज का आपका कार्ड',
    browserTitle: 'Telegram में खोलें',
    browserBody: 'टैरो केवल Telegram ऐप में काम करता है — वहाँ हम आपका खाता पहचानते हैं, इतिहास सहेजते हैं और 9:00 बजे दैनिक कार्ड भेजते हैं।',
    openInTelegram: 'Telegram में खोलें',
  },
  pt: {
    title: 'Carta do dia', loading: 'Embaralhando…', error: 'Não foi possível tirar a carta.', already: 'Sua carta de hoje',
    browserTitle: 'Abra no Telegram',
    browserBody: 'O Tarô funciona apenas dentro do Telegram — é lá que reconhecemos sua conta, salvamos o histórico e enviamos sua carta do dia às 9:00.',
    openInTelegram: 'Abrir no Telegram',
  },
  es: {
    title: 'Carta del día', loading: 'Barajando…', error: 'No se pudo sacar la carta.', already: 'Tu carta de hoy',
    browserTitle: 'Abre en Telegram',
    browserBody: 'El Tarot funciona solo dentro de Telegram — allí reconocemos tu cuenta, guardamos el historial y enviamos tu carta del día a las 9:00.',
    openInTelegram: 'Abrir en Telegram',
  },
  de: {
    title: 'Karte des Tages', loading: 'Mische…', error: 'Karte konnte nicht gezogen werden.', already: 'Deine Karte für heute',
    browserTitle: 'In Telegram öffnen',
    browserBody: 'Tarot läuft nur in Telegram — dort erkennen wir dein Konto, speichern Verlauf und senden um 9:00 die Tageskarte.',
    openInTelegram: 'In Telegram öffnen',
  },
  fr: {
    title: 'Carte du jour', loading: 'Mélange…', error: 'Impossible de tirer la carte.', already: 'Ta carte d\'aujourd\'hui',
    browserTitle: 'Ouvrir dans Telegram',
    browserBody: 'Le Tarot fonctionne uniquement dans Telegram — c\'est là que nous reconnaissons ton compte, sauvegardons l\'historique et envoyons ta carte du jour à 9:00.',
    openInTelegram: 'Ouvrir dans Telegram',
  },
};

const BOT_USERNAME = 'thisisthedailytarotbot';

function detectLang(): string {
  const nav = navigator.language?.toLowerCase().split('-')[0] ?? 'en';
  return nav in FALLBACK_COPY ? nav : 'en';
}

export function App() {
  const [card, setCard] = useState<PullResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inTelegram, setInTelegram] = useState(false);

  useEffect(() => {
    // @ts-expect-error — Telegram injects WebApp global
    const tg = window.Telegram?.WebApp;
    tg?.ready?.();
    tg?.expand?.();

    const hasInit = hasTelegramInitData();
    setInTelegram(hasInit);

    if (!hasInit) {
      setLoading(false);
      return;
    }

    const languageCode = tg?.initDataUnsafe?.user?.language_code as string | undefined;
    pullDailyCard(languageCode)
      .then(setCard)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (!inTelegram) {
    return <Landing lang={detectLang()} botUsername={BOT_USERNAME} />;
  }

  const lang = card?.lang ?? 'en';
  const copy = FALLBACK_COPY[lang] ?? FALLBACK_COPY.en!;

  return (
    <div className="app">
      <header className="app-header">
        <h1>{copy.title}</h1>
        {card?.alreadyPulled && <div className="app-subtitle">{copy.already}</div>}
      </header>

      <main className="app-main">
        {loading && <div className="app-loading">{copy.loading}</div>}
        {error && <div className="app-error">{copy.error}<br/><small>{error}</small></div>}
        {card && <CardView card={card} />}
        {card && (
          <UpsellPanel
            lang={card.lang}
            alreadyPulled={card.alreadyPulled}
            onPaid={() => { /* keep result inline; don't reload — preserves drawn card */ }}
          />
        )}
      </main>
    </div>
  );
}
